import React, { useEffect, useState, useMemo } from "react";
import Loader from "../../Shared/Loader";

import "../../../css/CentersTable.css";
import AddCampCenterModal from "./AddCampCenterModal";
import ViewCampCenterModal from "./ViewCampCenterModal";
import api from "../../../api/axiosConfig";

// Define the expanded interface for a Camp Center
interface CampCenter {
  _id: string;
  name: string;
  centerType: string;
  city: string;
  isActive: boolean;
  createdAt: string;
  disabledOn?: string;
  addressLine1?: string;
  addressLine2?: string;
  contactPhone1?: string;
  contactPhone2?: string;
  latitude?: number;
  longitude?: number;
}

const CampCenterTable: React.FC = () => {
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [centers, setCenters] = useState<CampCenter[]>([]);
  const [loading, setLoading] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState<CampCenter | null>(null);

  // 🔽 1. State for filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchCenters = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const res = await api.center_api.get(
        "/api/v1/centers?&type=CAMP CENTER",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCenters(res.data.data || []);
    } catch (err) {
      console.error("Error fetching camp centers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenters();
  }, []);

  // 🔽 2. Memoized filtering logic
  const filteredCenters = useMemo(() => {
    return centers
      .filter(c => {
        // Search by name or city
        return (
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.city.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      .filter(c => {
        // Filter by category
        if (categoryFilter === "All") return true;
        return c.centerType.trim() === categoryFilter.trim();
      })
      .filter(c => {
        // Filter by derived status
        if (statusFilter === "All") return true;
        const isActive = !c.disabledOn; // Active if disabledOn is null/undefined
        return statusFilter === "Active" ? isActive : !isActive;
      });
  }, [centers, searchTerm, categoryFilter, statusFilter]);
  
  // 🔽 3. Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter]);

  // 🔽 4. Pagination Logic
  const totalPages = Math.ceil(filteredCenters.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCentersToDisplay = filteredCenters.slice(startIndex, endIndex);

  const handleRowClick = (center: CampCenter) => {
    setSelectedCenter(center);
    setViewModalOpen(true);
  };

  return (
    <div>
      <div className="table-header">
        {/* --- Controlled Inputs --- */}
        <div className="global-filters-container">
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by name or city..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="global-select-input"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="All">All Types</option>
            <option value="CAMP CENTER">CAMP CENTER</option>
          </select>
          <select
            className="global-select-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <button className="add-btn" onClick={() => setAddModalOpen(true)}>
          + Add Camp Center
        </button>
      </div>

      <table className="center-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Center Name</th>
            <th>Center Type</th>
            <th>City</th>
            <th>Status</th> {/* Added Status column */}
            <th>Created On</th>
            <th>Disabled On</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7}><Loader inline text="Loading Camp Centers..." /></td></tr>
          ) : currentCentersToDisplay.length > 0 ? (
            currentCentersToDisplay.map((c, index) => (
              <tr key={c._id} onClick={() => handleRowClick(c)} style={{ cursor: "pointer" }}>
                <td>{startIndex + index + 1}</td>
                <td><b>{c.name}</b></td>
                <td>
                  <span className="badge type" style={{ background: "#c5e5eeff", color: "blue" }}>
                    {c.centerType}
                  </span>
                </td>
                <td>{c.city}</td>
                <td>
                  <span className={`status ${c.isActive ? "active" : "disabled"}`}>
                    {c.isActive ? "Active" : "Disabled"}
                  </span>
                </td>
                <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                <td>{c.disabledOn ? new Date(c.disabledOn).toLocaleDateString() : "-"}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan={7} style={{ textAlign: "center" }}>No centers match the current filters.</td></tr>
          )}
        </tbody>
      </table>

      {/* Modals */}
      <AddCampCenterModal isOpen={isAddModalOpen} onClose={() => setAddModalOpen(false)} onSuccess={fetchCenters} />
      <ViewCampCenterModal isOpen={isViewModalOpen} onClose={() => setViewModalOpen(false)} center={selectedCenter} onUpdateSuccess={fetchCenters} />
      
      {/* 🔽 5. Dynamic Pagination Footer */}
      {totalPages > 0 && (
        <div className="pagination-footer">
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredCenters.length)} of {filteredCenters.length} results
          </p>
          <div className="pagination">
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
              <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={currentPage === pageNumber ? "active" : ""}>
                {pageNumber}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampCenterTable;